import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  createCmsMessage,
  CreateMessagePayload,
  fetchCmsMessages,
  MessageGroup,
} from "../../apis/cmsMessageApi";

// ----------------------
// TYPES
// ----------------------

interface CmsMessageState {
  list: MessageGroup[];
  studyTeam: MessageGroup[];

  totalCountList: number;
  totalCountStudyTeam: number;

  loading: boolean;
  createLoading: boolean;
  error: string | null;
}

const initialState: CmsMessageState = {
  list: [],
  studyTeam: [],

  totalCountList: 0,
  totalCountStudyTeam: 0,

  loading: false,
  createLoading: false,
  error: null,
};

// ----------------------
// ASYNC THUNKS
// ----------------------

export const getCmsMessages = createAsyncThunk(
  "cmsMessages/getCmsMessages",
  async (
    {
      type,
      pageId = 1,
      limit = 30,
    }: { type: "list" | "study-team"; pageId?: number; limit?: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchCmsMessages(type, pageId, limit);

      return { type, data: response.data };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.data || "Failed to fetch messages",
      );
    }
  },
);

export const sendCmsMessage = createAsyncThunk(
  "cmsMessages/sendCmsMessage",
  async (payload: CreateMessagePayload, { rejectWithValue }) => {
    try {
      const response = await createCmsMessage(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.data || "Failed to send message",
      );
    }
  },
);

// ----------------------
// SLICE
// ----------------------

const cmsMessageSlice = createSlice({
  name: "cmsMessages",
  initialState,
  reducers: {
    clearCmsMessages: (state) => {
      state.list = [];
      state.studyTeam = [];
      state.totalCountList = 0;
      state.totalCountStudyTeam = 0;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ================================
         FETCH CMS MESSAGES
      ================================= */

      .addCase(getCmsMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(getCmsMessages.fulfilled, (state, action) => {
        state.loading = false;

        const groups = action.payload.data || [];

        const total =
          groups.length > 0 ? Number(groups[0]?.totalMessages || 0) : 0;

        if (action.payload.type === "list") {
          state.list = groups;
          state.totalCountList = total;
        } else if (action.payload.type === "study-team") {
          state.studyTeam = groups;
          state.totalCountStudyTeam = total;
        }
      })

      .addCase(getCmsMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(state.error || "Failed to fetch messages");
      })

      /* ================================
         SEND MESSAGE
      ================================= */

      .addCase(sendCmsMessage.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })

      .addCase(sendCmsMessage.fulfilled, (state, action) => {
        state.createLoading = false;

        const apiMessage = action.payload?.data || "Message sent successfully.";

        toast.success(apiMessage);
      })

      .addCase(sendCmsMessage.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload as string;
        toast.error(state.error || "Failed to send message");
      });
  },
});

export const { clearCmsMessages } = cmsMessageSlice.actions;

export default cmsMessageSlice.reducer;
